import { mergeItemArrays } from '@/services/mergeItems';
import type { Item } from '@/types';

interface TrackingIds {
  deletedIds: Set<string>;
  dirtyIds: Set<string>;
}

interface ItemWriteSchedulerOptions {
  debounceMs?: number;
  getTrackingIds: () => TrackingIds;
  onError?: (filename: string, error: unknown) => void;
  onWriteComplete?: (filename: string, data: Item[]) => void;
  readRemote: (filename: string) => Promise<Item[]>;
  writeRemote: (filename: string, data: Item[]) => Promise<void>;
}

interface ItemWriteScheduler {
  dispose: () => void;
  flush: (filename: string) => Promise<void>;
  schedule: (filename: string, localItems: Item[]) => void;
}

export async function withCrossTabWriteLock<T>(
  filename: string,
  task: () => Promise<T>
): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return task();
  }

  return navigator.locks.request(
    `personal-insights-write:${filename}`,
    { mode: 'exclusive' },
    task
  );
}

export function createItemWriteScheduler({
  debounceMs = 500,
  getTrackingIds,
  onError,
  onWriteComplete,
  readRemote,
  writeRemote,
}: ItemWriteSchedulerOptions): ItemWriteScheduler {
  const inFlightWrites = new Map<string, Promise<void>>();
  const pendingItems = new Map<string, Item[]>();
  const writeTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  const queueWrite = (filename: string): Promise<void> => {
    const localItems = pendingItems.get(filename);
    if (!localItems) return Promise.resolve();

    const previousWrite = inFlightWrites.get(filename) ?? Promise.resolve();
    const nextWrite = previousWrite
      .catch(() => {})
      .then(async () => {
        try {
          await withCrossTabWriteLock(filename, async () => {
            const remoteItems = await readRemote(filename);
            const { dirtyIds, deletedIds } = getTrackingIds();
            const { merged } = mergeItemArrays(localItems, remoteItems, dirtyIds, deletedIds);
            await writeRemote(filename, merged);

            if (pendingItems.get(filename) === localItems) {
              pendingItems.delete(filename);
            }

            onWriteComplete?.(filename, merged);
          });
        } catch (error) {
          onError?.(filename, error);
        }
      })
      .finally(() => {
        if (inFlightWrites.get(filename) === nextWrite) {
          inFlightWrites.delete(filename);
        }
      });

    inFlightWrites.set(filename, nextWrite);
    return nextWrite;
  };

  const schedule = (filename: string, localItems: Item[]) => {
    pendingItems.set(filename, localItems);

    const existingTimeout = writeTimeouts.get(filename);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      writeTimeouts.delete(filename);
      void queueWrite(filename);
    }, debounceMs);

    writeTimeouts.set(filename, timeout);
  };

  const flush = async (filename: string) => {
    const timeout = writeTimeouts.get(filename);
    if (timeout) {
      clearTimeout(timeout);
      writeTimeouts.delete(filename);
    }

    await queueWrite(filename);
  };

  const dispose = () => {
    for (const timeout of writeTimeouts.values()) {
      clearTimeout(timeout);
    }

    writeTimeouts.clear();
    pendingItems.clear();
  };

  return {
    dispose,
    flush,
    schedule,
  };
}
